import { formatDuration } from "./formatDuration";

// The eight stat types Android pins to its four TaskList slots — here we show
// all eight at once. Order mirrors Android's TaskListStatType (recommended
// priority): total → done → spent → billing → estimated → actions → ai → sessions.
export const STAT_TYPES = [
  {
    key: "TOTAL_TASKS",
    label: "Total Tasks",
    icon: "📋",
    description: "How many tasks live in the active project.",
  },
  {
    key: "DONE_TASKS",
    label: "Done Tasks",
    icon: "✅",
    description: "Tasks marked Done, your completion signal.",
  },
  {
    key: "SPENT_TIME",
    label: "Spent Time",
    icon: "⏱️",
    description: "Total hours actually logged across every tracked session.",
  },
  {
    key: "BILLING",
    label: "Billing",
    icon: "💰",
    description: "Earnings so far, hours times your hourly rate.",
  },
  {
    key: "ESTIMATED_TIME",
    label: "Estimated Time",
    icon: "⏳",
    description: "Hours you planned for these tasks. Compare against Spent Time.",
  },
  {
    key: "TOTAL_ACTIONS",
    label: "Total Actions",
    icon: "⚡",
    description: "All named action steps across tasks in scope.",
  },
  {
    key: "AI_HOURS",
    label: "AI Hours",
    icon: "🤖",
    description: "Time tracked under the AI rate (not used on web yet).",
  },
  {
    key: "SESSIONS",
    label: "Sessions",
    icon: "🔁",
    description: "Count of time records (every start and stop).",
  },
];

export const STAT_TYPE_BY_KEY = STAT_TYPES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

const MILLIS_PER_HOUR = 3_600_000;

/**
 * Compute the raw stat values for a project's tasks + tracking data.
 *
 * @param {Object} input
 *   tasks          — the project's tasks (notes excluded by the caller)
 *   totalSpentTime — sum of time-record spentTime (ms)
 *   aiSpentTime    — sum of AI-tracked spentTime (ms); 0 on web (no AI sessions)
 *   sessionCount   — number of time records
 *   totalActions   — number of actions across the project's tasks
 *   hourlyRate     — human billing rate (currency/hour)
 *   aiHourlyRate   — AI billing rate (currency/hour)
 *   aiInvoiceEnabled — when true, AI-tracked time bills at the AI rate;
 *                      when false, everything bills at the human rate
 *                      (mirrors Android's ProjectTotalBillingFlow.rateFor)
 */
export function computeStats({
  tasks = [],
  totalSpentTime = 0,
  aiSpentTime = 0,
  sessionCount = 0,
  totalActions = 0,
  hourlyRate = 0,
  aiHourlyRate = 0,
  aiInvoiceEnabled = true,
}) {
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(
    (t) => t.taskState === "task_state_done"
  ).length;
  // estimatedTime is a per-task Float in hours (Android TaskData.estimatedTime).
  const estimatedHours = tasks.reduce(
    (sum, t) => sum + (Number(t.estimatedTime) || 0),
    0
  );
  // Billing — split human vs AI time when AI invoicing is on (matches Android):
  //   AI on  → humanHours × humanRate + aiHours × aiRate
  //   AI off → totalHours × humanRate
  const humanMs = Math.max(0, totalSpentTime - aiSpentTime);
  const billing = aiInvoiceEnabled
    ? (humanMs / MILLIS_PER_HOUR) * hourlyRate +
      (aiSpentTime / MILLIS_PER_HOUR) * aiHourlyRate
    : (totalSpentTime / MILLIS_PER_HOUR) * hourlyRate;

  return {
    TOTAL_TASKS: totalTasks,
    DONE_TASKS: doneTasks,
    SPENT_TIME: totalSpentTime,
    BILLING: billing,
    ESTIMATED_TIME: estimatedHours,
    TOTAL_ACTIONS: totalActions,
    AI_HOURS: aiSpentTime,
    SESSIONS: sessionCount,
  };
}

// Whether a computed value counts as "empty" (drives SHOW_NON_EMPTY). Mirrors
// Android's isStatEmpty: every type is empty at its zero value.
export function isStatEmpty(key, value) {
  return !value; // 0, 0.0, "" all count as empty
}

// Format a raw value for display, matching Android's per-type formatting.
// `currencyCode` is the active currency (e.g. "USD"); billing renders as
// "3848.10 USD" — Android's formatWithoutSymbol, since the card already shows a
// money icon beside the value.
export function formatStat(key, value, currencyCode = "USD") {
  switch (key) {
    case "SPENT_TIME":
    case "AI_HOURS":
      // Cumulative totals can exceed 24h — use a non-wrapping duration format
      // (formatTimeToClock wraps hours at 24, e.g. 166h → "22:..").
      return formatDuration(value);
    case "BILLING":
      return `${(value || 0).toFixed(2)} ${currencyCode}`;
    case "ESTIMATED_TIME":
      return `${(value || 0).toFixed(1)} h`;
    default:
      return String(value || 0);
  }
}
